import { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';

const StudentQuiz = () => {
  const [view,       setView]       = useState('subjects'); // subjects | type | attempt | result
  const [quizzes,    setQuizzes]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [answers,    setAnswers]    = useState({});
  const [result,     setResult]     = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [currentQ,   setCurrentQ]   = useState(0);
  const [activeSubj, setActiveSubj] = useState(null); // selected subject
  const [activeType, setActiveType] = useState(null); // selected assessment type
  const startTimeRef = useRef(null);

  useEffect(()=>{ fetchQuizzes(); },[]);

  const fetchQuizzes = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/student/quizzes');
      setQuizzes(data);
    } catch { toast.error('Failed to load assessments'); }
    finally   { setLoading(false); }
  };

  // ── Group quizzes: subject → assessmentType → [quizzes] ──────────────────
  const grouped = quizzes.reduce((acc, q) => {
    const subj = q.subject || 'General';
    const type = q.assessmentType?.name || q.assessmentTypeName || 'Quiz';
    if (!acc[subj]) acc[subj] = {};
    if (!acc[subj][type]) acc[subj][type] = [];
    acc[subj][type].push(q);
    return acc;
  }, {});

  const subjects = Object.keys(grouped);

  const DIFF_LABELS = { easy:'🟢 Easy', medium:'🟡 Medium', hard:'🔴 Hard' };
  const pctColor    = (p) => p>=80?'#10c98e':p>=60?'#f59e0b':'#ef4444';
  const pctBg       = (p) => p>=80?'#ecfdf5':p>=60?'#fffbeb':'#fef2f2';

  const subjColors = ['#8b5cf6','#3b6ef8','#10c98e','#f59e0b','#ef4444','#06b6d4','#ec4899'];
  const subjColor  = (i) => subjColors[i % subjColors.length];

  const startQuiz = async (quiz) => {
    if (quiz.attempt) { toast.error('Already attempted'); return; }
    if (quiz.status==='closed') { toast.error('This assessment is closed'); return; }
    try {
      const { data } = await api.get(`/student/quizzes/${quiz._id}`);
      setActiveQuiz(data); setAnswers({}); setCurrentQ(0);
      startTimeRef.current = Date.now(); setView('attempt');
    } catch { toast.error('Failed to load assessment'); }
  };

  const handleAnswer = (qIndex, idx) => setAnswers(prev=>({...prev,[qIndex]:idx}));

  const handleSubmit = async () => {
    const unanswered = activeQuiz.questions.filter((q,i)=>answers[i]===undefined).length;
    if (unanswered>0 && !window.confirm(`${unanswered} unanswered. Submit anyway?`)) return;
    setSubmitting(true);
    try {
      const timeTaken = Math.floor((Date.now()-startTimeRef.current)/1000);
      const answersArr = activeQuiz.questions.map((q,i)=>({
        questionId: q._id,
        selectedIndex: answers[i]!==undefined ? answers[i] : null,
      }));
      const { data } = await api.post(`/student/quizzes/${activeQuiz._id}/submit`,{answers:answersArr,timeTaken});
      setResult(data); setView('result'); fetchQuizzes();
    } catch(err) { toast.error(err.response?.data?.message||'Failed to submit'); }
    finally { setSubmitting(false); }
  };

  const qzStyle = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
    .sq * { box-sizing:border-box; }
    .sq { font-family:'Sora',sans-serif; color:#1a1d2e; animation:sqFade .4s ease both; }
    @keyframes sqFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
    .sq-title { font-size:1.25rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; margin-bottom:4px; }
    .sq-sub   { font-size:.76rem; color:#9094a8; margin-bottom:1.4rem; }

    .btn { display:inline-flex; align-items:center; gap:6px; padding:8px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
    .btn-blue   { background:#3b6ef8; color:white; }
    .btn-blue:hover { background:#2d5ef0; }
    .btn-purple { background:#8b5cf6; color:white; }
    .btn-purple:hover { background:#7c3aed; }
    .btn-green  { background:#10c98e; color:white; }
    .btn-green:hover { background:#0db87e; }
    .btn-ghost  { background:#f0f2f7; color:#6b7194; }
    .btn-ghost:hover { background:#e4e7f0; }
    .btn-sm { padding:5px 11px; font-size:.74rem; }
    .btn:disabled { opacity:.5; cursor:not-allowed; }

    /* ── Subject cards ── */
    .subj-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(220px,1fr)); gap:1rem; margin-bottom:1rem; }
    .subj-card { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.2rem 1.4rem; cursor:pointer; transition:all .18s; }
    .subj-card:hover { transform:translateY(-2px); box-shadow:0 6px 20px rgba(0,0,0,.09); }
    .subj-card-icon { width:42px; height:42px; border-radius:12px; display:flex; align-items:center; justify-content:center; font-size:.9rem; font-weight:700; color:white; margin-bottom:.9rem; letter-spacing:.02em; }
    .subj-card-name { font-size:.96rem; font-weight:700; color:#0f1123; margin-bottom:4px; }
    .subj-card-meta { font-size:.72rem; color:#9094a8; margin-bottom:.8rem; }
    .subj-card-types { display:flex; flex-direction:column; gap:5px; }
    .subj-type-row { display:flex; align-items:center; justify-content:space-between; font-size:.76rem; }
    .subj-type-name { color:#6b7194; font-weight:500; }
    .subj-type-count { font-weight:700; color:#0f1123; font-family:'JetBrains Mono',monospace; font-size:.74rem; }
    .subj-card-footer { margin-top:.9rem; padding-top:.8rem; border-top:1px solid #f0f2f8; font-size:.72rem; color:#9094a8; display:flex; align-items:center; gap:4px; }

    /* ── Assessment type list ── */
    .back-btn { display:inline-flex; align-items:center; gap:6px; padding:7px 14px; border:1.5px solid #e0e4f0; border-radius:10px; background:white; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; color:#6b7194; transition:all .15s; margin-bottom:1rem; }
    .back-btn:hover { border-color:#8b5cf6; color:#8b5cf6; }

    .type-card { background:white; border-radius:14px; border:1px solid #e8ebf4; margin-bottom:1rem; overflow:hidden; }
    .type-card-header { display:flex; align-items:center; justify-content:space-between; padding:12px 18px; background:#fafbff; border-bottom:1px solid #edf0f9; cursor:pointer; user-select:none; }
    .type-card-header:hover { background:#f3f0ff; }
    .type-card-name { font-size:.88rem; font-weight:700; color:#0f1123; }
    .type-card-count { font-size:.72rem; color:#9094a8; }

    /* ── Quiz card (inside type) ── */
    .quiz-row { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f3f5fb; gap:12px; flex-wrap:wrap; }
    .quiz-row:last-child { border-bottom:none; }
    .quiz-row-left { flex:1; min-width:0; }
    .quiz-row-title { font-size:.86rem; font-weight:700; color:#0f1123; margin-bottom:3px; }
    .quiz-row-meta  { font-size:.72rem; color:#9094a8; }
    .quiz-score-chip { display:inline-flex; align-items:center; padding:5px 13px; border-radius:99px; font-family:'JetBrains Mono',monospace; font-size:.88rem; font-weight:700; white-space:nowrap; }

    .pill { display:inline-flex; font-size:.66rem; font-weight:600; padding:2px 8px; border-radius:99px; margin-right:4px; white-space:nowrap; }
    .pill-blue{background:#eef2ff;color:#3b6ef8} .pill-green{background:#ecfdf5;color:#10c98e}
    .pill-amber{background:#fffbeb;color:#f59e0b} .pill-red{background:#fef2f2;color:#ef4444}
    .pill-gray{background:#f3f4f6;color:#6b7280}  .pill-purple{background:#f3f0ff;color:#8b5cf6}

    /* ── Attempt ── */
    .attempt-header { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:14px 18px; margin-bottom:1rem; display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:8px; }
    .q-progress { display:flex; gap:4px; flex-wrap:wrap; margin-bottom:1rem; }
    .q-dot { width:28px; height:28px; border-radius:8px; border:2px solid #e0e4f0; background:white; font-size:.68rem; font-weight:700; display:flex; align-items:center; justify-content:center; cursor:pointer; transition:all .15s; color:#9094a8; flex-shrink:0; }
    .q-dot.answered { border-color:#8b5cf6; background:#8b5cf6; color:white; }
    .q-dot.current  { border-color:#3b6ef8; background:#eef2ff; color:#3b6ef8; }
    .q-card { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:20px; margin-bottom:1rem; }
    .q-num  { font-size:.68rem; font-weight:700; color:#8b5cf6; text-transform:uppercase; letter-spacing:.06em; margin-bottom:8px; }
    .q-text { font-size:.96rem; font-weight:600; color:#0f1123; line-height:1.55; margin-bottom:16px; }
    .option-btn { width:100%; padding:12px 16px; border-radius:12px; border:2px solid #e0e4f0; background:white; font-family:'Sora',sans-serif; font-size:.84rem; text-align:left; cursor:pointer; transition:all .15s; display:flex; align-items:center; gap:12px; margin-bottom:8px; color:#1a1d2e; }
    .option-btn:hover  { border-color:#8b5cf6; background:#faf5ff; }
    .option-btn.selected { border-color:#8b5cf6; background:#f3f0ff; color:#7c3aed; font-weight:600; }
    .option-letter { width:28px; height:28px; border-radius:99px; background:#f0f2f7; font-size:.72rem; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
    .option-btn.selected .option-letter { background:#8b5cf6; color:white; }
    .nav-row { display:flex; justify-content:space-between; gap:8px; }
    .nav-row .btn { flex:1; justify-content:center; }

    /* ── Result ── */
    .result-card { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:24px; margin-bottom:1rem; text-align:center; }
    .result-score { font-size:3rem; font-weight:700; font-family:'JetBrains Mono',monospace; margin-bottom:4px; }
    .result-label { font-size:.82rem; color:#9094a8; margin-bottom:16px; }
    .q-result-item { background:white; border-radius:12px; border:1px solid #e8ebf4; padding:14px 16px; margin-bottom:8px; }
    .q-result-item.correct { border-color:#a7f3d0; background:#ecfdf5; }
    .q-result-item.wrong   { border-color:#fecaca; background:#fef2f2; }
    .q-result-item.skipped { border-color:#e0e4f0; background:#f8f9fc; }
    .q-result-text { font-size:.84rem; font-weight:600; color:#0f1123; margin-bottom:8px; }
    .q-result-expl { font-size:.72rem; color:#6b7194; background:#f0f9ff; border-radius:6px; padding:5px 9px; border-left:2px solid #3b6ef8; margin-top:6px; }

    .spinner { width:16px; height:16px; border:2px solid #e4e7f0; border-top-color:#8b5cf6; border-radius:50%; animation:spin .7s linear infinite; display:inline-block; }
    @keyframes spin { to{transform:rotate(360deg);} }
    .empty { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }

    @media (max-width:600px) {
      .subj-grid { grid-template-columns:1fr; }
      .sq-title  { font-size:1.1rem; }
    }
  `;

  // ── ATTEMPT VIEW ─────────────────────────────────────────────────────────
  if (view==='attempt' && activeQuiz) {
    const q       = activeQuiz.questions[currentQ];
    const answered = Object.keys(answers).length;
    return (
      <div className="sq"><style>{qzStyle}</style>
        <div className="attempt-header">
          <div>
            <div style={{fontWeight:700,fontSize:'.92rem',color:'#0f1123'}}>{activeQuiz.title}</div>
            <div style={{fontSize:'.72rem',color:'#9094a8'}}>{answered}/{activeQuiz.questions.length} answered</div>
          </div>
          <button className="btn btn-green" onClick={handleSubmit} disabled={submitting}>
            {submitting?<><span className="spinner" style={{width:14,height:14}}/> Submitting…</>:'✓ Submit'}
          </button>
        </div>
        <div className="q-progress">
          {activeQuiz.questions.map((q,i)=>(
            <div key={i} className={`q-dot ${answers[i]!==undefined?'answered':''} ${i===currentQ?'current':''}`} onClick={()=>setCurrentQ(i)}>{i+1}</div>
          ))}
        </div>
        <div className="q-card">
          <div className="q-num">Question {currentQ+1} of {activeQuiz.questions.length}</div>
          <div className="q-text">{q.question}</div>
          {q.options?.map((opt,j)=>(
            <button key={j} className={`option-btn ${answers[currentQ]===j?'selected':''}`} onClick={()=>handleAnswer(currentQ,j)}>
              <span className="option-letter">{String.fromCharCode(65+j)}</span>{opt}
            </button>
          ))}
        </div>
        <div className="nav-row">
          <button className="btn btn-ghost" onClick={()=>setCurrentQ(i=>Math.max(0,i-1))} disabled={currentQ===0}>← Prev</button>
          {currentQ<activeQuiz.questions.length-1
            ? <button className="btn btn-blue" onClick={()=>setCurrentQ(i=>i+1)}>Next →</button>
            : <button className="btn btn-green" onClick={handleSubmit} disabled={submitting}>{submitting?'Submitting…':'Submit ✓'}</button>
          }
        </div>
      </div>
    );
  }

  // ── RESULT VIEW ──────────────────────────────────────────────────────────
  if (view==='result' && result) {
    const { score, totalQuestions, percentage, result:qResults } = result;
    const color = pctColor(percentage);
    return (
      <div className="sq"><style>{qzStyle}</style>
        <div className="result-card">
          <div style={{fontSize:'2rem',marginBottom:8}}>{percentage>=80?'🎉':percentage>=50?'👍':'📚'}</div>
          <div className="result-score" style={{color}}>{percentage}%</div>
          <div className="result-label">{score}/{totalQuestions} correct</div>
          <div style={{fontSize:'.78rem',color:'#6b7194',marginBottom:12}}>{percentage>=80?'Excellent! 🌟':percentage>=50?'Good effort! Review wrong answers.':'Keep studying!'}</div>
          <button className="btn btn-ghost" onClick={()=>{ setView('subjects'); setResult(null); }}>← Back to Assessments</button>
        </div>
        <div style={{fontWeight:700,fontSize:'.88rem',color:'#0f1123',marginBottom:10}}>Question Review</div>
        {qResults?.map((q,i)=>(
          <div key={i} className={`q-result-item ${q.selectedIndex===null?'skipped':q.isCorrect?'correct':'wrong'}`}>
            <div className="q-result-text">{i+1}. {q.question}</div>
            <div style={{fontSize:'.78rem',marginBottom:4,display:'flex',flexWrap:'wrap',gap:4,flexDirection:'column'}}>
              {q.selectedIndex!==null
                ? <span style={{color:q.isCorrect?'#10c98e':'#ef4444'}}>Your answer: {String.fromCharCode(65+(q.selectedIndex||0))}. {q.options?.[q.selectedIndex]}</span>
                : <span style={{color:'#9094a8'}}>Skipped</span>}
              {!q.isCorrect&&q.selectedIndex!==null&&<span style={{color:'#10c98e'}}>✓ Correct: {String.fromCharCode(65+q.correctIndex)}. {q.options?.[q.correctIndex]}</span>}
              {!q.isCorrect&&q.selectedIndex===null&&<span style={{color:'#10c98e'}}>✓ Was: {String.fromCharCode(65+q.correctIndex)}. {q.options?.[q.correctIndex]}</span>}
            </div>
            {q.explanation&&<div className="q-result-expl">💡 {q.explanation}</div>}
          </div>
        ))}
      </div>
    );
  }

  // ── TYPE DRILLDOWN VIEW ───────────────────────────────────────────────────
  if (view==='type' && activeSubj) {
    const types = grouped[activeSubj] || {};
    return (
      <div className="sq"><style>{qzStyle}</style>
        <button className="back-btn" onClick={()=>{ setView('subjects'); setActiveSubj(null); }}>← All Subjects</button>
        <div style={{marginBottom:'1rem'}}>
          <div className="sq-title">{activeSubj}</div>
          <div className="sq-sub">Select an assessment type</div>
        </div>
        {Object.entries(types).map(([typeName, typeQuizzes]) => (
          <div key={typeName} className="type-card">
            <div className="type-card-header" onClick={()=>{ setActiveType(typeName); setView('quizlist'); }}>
              <div>
                <div className="type-card-name">{typeName}</div>
                <div className="type-card-count">{typeQuizzes.length} assessment{typeQuizzes.length!==1?'s':''}</div>
              </div>
              <span style={{fontSize:'.82rem',color:'#9094a8'}}>→</span>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // ── QUIZ LIST VIEW (subject + type selected) ──────────────────────────────
  if (view==='quizlist' && activeSubj && activeType) {
    const typeQuizzes = grouped[activeSubj]?.[activeType] || [];
    return (
      <div className="sq"><style>{qzStyle}</style>
        <button className="back-btn" onClick={()=>setView('type')}>← {activeSubj}</button>
        <div style={{marginBottom:'1rem'}}>
          <div className="sq-title">{activeType}</div>
          <div className="sq-sub">{activeSubj} · {typeQuizzes.length} assessment{typeQuizzes.length!==1?'s':''}</div>
        </div>

        {typeQuizzes.length===0 ? (
          <div className="empty">No assessments here yet.</div>
        ) : typeQuizzes.map(q => {
          const attempted = !!q.attempt;
          const pct       = q.attempt?.percentage;
          const score     = q.attempt?.score;
          const total     = q.attempt?.totalQuestions;
          return (
            <div key={q._id} style={{background:'white',borderRadius:14,border:'1px solid #e8ebf4',padding:'1.1rem 1.2rem',marginBottom:'.8rem'}}>
              <div className="quiz-row" style={{padding:0,border:'none',flexWrap:'nowrap'}}>
                <div className="quiz-row-left">
                  <div className="quiz-row-title">{q.title}</div>
                  <div className="quiz-row-meta">
                    {q.numQuestions} questions
                    {q.dueDate && ` · Due: ${new Date(q.dueDate).toLocaleDateString('en-PK',{day:'numeric',month:'short',year:'numeric'})}`}
                  </div>
                  <div style={{marginTop:6,display:'flex',flexWrap:'wrap',gap:4}}>
                    <span className={`pill ${q.difficulty==='easy'?'pill-green':q.difficulty==='medium'?'pill-amber':'pill-red'}`}>{DIFF_LABELS[q.difficulty]||q.difficulty}</span>
                    {attempted
                      ? <span className="pill pill-green">✓ Submitted</span>
                      : q.status==='closed'
                        ? <span className="pill pill-gray">Closed</span>
                        : <span className="pill pill-blue">• Pending</span>}
                  </div>
                </div>
                <div style={{flexShrink:0}}>
                  {attempted ? (
                    <span className="quiz-score-chip" style={{background:pctBg(pct),color:pctColor(pct)}}>
                      {score}/{total}
                    </span>
                  ) : q.status==='assigned' ? (
                    <button className="btn btn-blue" onClick={()=>startQuiz(q)}>Start →</button>
                  ) : (
                    <span style={{fontSize:'.74rem',color:'#9094a8'}}>Closed</span>
                  )}
                </div>
              </div>
              {attempted && (
                <div style={{marginTop:10,background:'#f8f9fc',borderRadius:8,padding:'8px 12px',fontSize:'.76rem',color:'#6b7194',display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:6}}>
                  <span>Score: <strong style={{fontFamily:'JetBrains Mono,monospace',color:pctColor(pct)}}>{score}/{total}</strong></span>
                  <span style={{color:pctColor(pct),fontWeight:700}}>{pct}%</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // ── SUBJECTS OVERVIEW ─────────────────────────────────────────────────────
  return (
    <div className="sq"><style>{qzStyle}</style>
      <div style={{marginBottom:'1rem'}}>
        <div className="sq-title">Online Assessments</div>
        <div className="sq-sub">Select a subject to see your assigned assessments</div>
      </div>

      {loading ? (
        <div style={{textAlign:'center',padding:'3rem',color:'#9094a8'}}><span className="spinner"/></div>
      ) : subjects.length===0 ? (
        <div style={{background:'white',borderRadius:16,border:'1px solid #e8ebf4',padding:'3rem 2rem',textAlign:'center'}}>
          <div style={{fontSize:'2rem',marginBottom:10}}>📝</div>
          <div style={{fontWeight:700,fontSize:'.92rem',color:'#0f1123',marginBottom:4}}>No assessments assigned yet</div>
          <div style={{fontSize:'.76rem',color:'#9094a8'}}>Check back once your teacher assigns one.</div>
        </div>
      ) : (
        <div className="subj-grid">
          {subjects.map((subj, idx) => {
            const types = grouped[subj];
            const allQuizzes = Object.values(types).flat();
            const totalCount = allQuizzes.length;
            const attemptedCount = allQuizzes.filter(q=>q.attempt).length;
            const pendingCount = allQuizzes.filter(q=>!q.attempt && q.status==='assigned').length;
            const color = subjColor(idx);
            return (
              <div key={subj} className="subj-card" onClick={()=>{ setActiveSubj(subj); setView('type'); }}
                style={{borderTop:`3px solid ${color}`}}>
                <div className="subj-card-icon" style={{background:color}}>
                  {subj.slice(0,3).toUpperCase()}
                </div>
                <div className="subj-card-name">{subj}</div>
                <div className="subj-card-meta">{Object.keys(types).length} type{Object.keys(types).length!==1?'s':''} · {totalCount} total</div>
                <div className="subj-card-types">
                  {Object.entries(types).map(([typeName, qs]) => (
                    <div key={typeName} className="subj-type-row">
                      <span className="subj-type-name">{typeName}</span>
                      <span className="subj-type-count">{qs.filter(q=>q.attempt).length}/{qs.length}</span>
                    </div>
                  ))}
                </div>
                <div className="subj-card-footer">
                  {pendingCount>0
                    ? <><span style={{width:8,height:8,borderRadius:'50%',background:'#3b6ef8',display:'inline-block'}}/>{pendingCount} pending</>
                    : attemptedCount===totalCount && totalCount>0
                      ? <><span style={{color:'#10c98e'}}>✓</span> All done</>
                      : <><span style={{color:'#9094a8'}}>○</span> {attemptedCount}/{totalCount} done</>
                  }
                  <span style={{marginLeft:'auto',fontSize:'.8rem'}}>→</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StudentQuiz;